import { createTheme } from '@mui/material';

const DEFAULT_FONT = 'Inter, sans-serif';
const fontOptions = ['Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Poppins'];

/**
 * Converts a font index or font name to a valid font family string
 * @param font - Font index (e.g., "4") or font name (e.g., "Montserrat")
 * @returns Valid font family string with fallback
 */
export const normalizeFont = (font: string | number | undefined): string => {
    if (font === undefined || font === null || font === '') {
        return DEFAULT_FONT;
    }

    // Check if it's a number or numeric string (index)
    const fontIndex = typeof font === 'number' ? font : parseInt(String(font), 10);
    if (!isNaN(fontIndex) && fontIndex >= 0 && fontIndex <= fontOptions.length) {
        return `${fontOptions[fontIndex - 1]}`;
    }

    // Check if it's a valid font name from fontOptions
    const fontString = String(font).trim();
    if (fontOptions.some(f => f.toLowerCase() === fontString.toLowerCase())) {
        return `${fontString}`;
    }

    // If it looks like a font family string already, use it
    if (fontString.length > 0) {
        return fontString.includes(',') ? fontString : `${fontString}`;
    }

    return DEFAULT_FONT;
};

export const breakpoints = createTheme().breakpoints;

/**
 * Returns MUI typography configuration for the theme
 * @param normalizedFont - Normalized font family string
 */
export const getTypography = (normalizedFont: string) => ({
    fontFamily: normalizedFont,
    fontWeightRegular: 400,
    fontWeightMedium: 600,
    fontWeightBold: 700,
    h1: {
        fontWeight: 400,
        lineHeight: 'normal',
        fontSize: 60,
        [breakpoints.down('sm')]: {
            fontSize: 24,
        },
        [breakpoints.between('sm', 'md')]: {
            fontSize: 32,
        },
    },
    h2: {
        fontWeight: 400,
        lineHeight: 'normal',
        fontSize: 48,
        [breakpoints.down('sm')]: {
            fontSize: 20,
        },
        [breakpoints.between('sm', 'md')]: {
            fontSize: 28,
        },
    },
    h3: {
        fontWeight: 400,
        lineHeight: 'normal',
        fontSize: 28,
        [breakpoints.down('sm')]: {
            fontSize: 18,
        },
        [breakpoints.between('sm', 'md')]: {
            fontSize: 22,
        },
    },
    h4: {
        fontWeight: 400,
        lineHeight: 'normal',
        fontSize: 24,
        [breakpoints.down('sm')]: {
            fontSize: 16,
        },
        [breakpoints.between('sm', 'md')]: {
            fontSize: 20,
        },
    },
    h5: {
        fontWeight: 600,
        lineHeight: 'normal',
        fontSize: 20,
        [breakpoints.down('sm')]: {
            fontSize: 14,
        },
        [breakpoints.between('sm', 'md')]: {
            fontSize: 17,
        },
    },
    h6: {
        fontWeight: 600,
        lineHeight: 'normal',
        fontSize: 16,
        [breakpoints.down('sm')]: {
            fontSize: 13,
        },
        [breakpoints.between('sm', 'md')]: {
            fontSize: 14.5,
        },
    },
    subtitle1: {
        fontWeight: 600,
        lineHeight: 'normal',
        fontSize: 14,
        [breakpoints.down('sm')]: {
            fontSize: 12,
        },
        [breakpoints.between('sm', 'md')]: {
            fontSize: 13,
        },
    },
    subtitle2: {
        fontWeight: 600,
        lineHeight: 'normal',
        fontSize: 12,
        [breakpoints.down('sm')]: {
            fontSize: 10,
        },
        [breakpoints.between('sm', 'md')]: {
            fontSize: 11,
        },
    },
    body1: {
        lineHeight: 'normal',
        fontSize: 16,
        fontWeight: 400,
        [breakpoints.down('sm')]: {
            fontSize: 13,
        },
        [breakpoints.between('sm', 'md')]: {
            fontSize: 14.5,
        },
    },
    body2: {
        lineHeight: 'normal',
        fontSize: 14,
        fontWeight: 400,
        [breakpoints.down('sm')]: {
            fontSize: 12,
        },
        [breakpoints.between('sm', 'md')]: {
            fontSize: 13,
        },
    },
    caption: {
        lineHeight: 'normal',
        fontSize: 12,
        fontWeight: 400,
        [breakpoints.down('sm')]: {
            fontSize: 10,
        },
        [breakpoints.between('sm', 'md')]: {
            fontSize: 11,
        },
    },
    overline: {
        fontWeight: 400,
        lineHeight: 'normal',
        fontSize: 10,
        textTransform: 'capitalize',
        [breakpoints.down('sm')]: {
            fontSize: 8,
        },
        [breakpoints.between('sm', 'md')]: {
            fontSize: 9,
        },
    },
    button: {
        fontWeight: 400,
        lineHeight: 'normal',
        fontSize: 14,
        textTransform: 'inherit',
        [breakpoints.down('sm')]: {
            fontSize: 12,
        },
        [breakpoints.between('sm', 'md')]: {
            fontSize: 13,
        },
    },
});
